/**
 * Pending KYC State Management
 *
 * Manages localStorage persistence for KYC link during ToS → KYC flow.
 * Survives page refreshes and cross-domain redirects.
 *
 * TTL: 5 minutes (prevents stale links)
 */

const PENDING_KYC_LINK_KEY = 'supabank:pending_kyc_link';
const PENDING_KYC_TS_KEY = 'supabank:pending_kyc_timestamp';
const PENDING_KYC_MAX_AGE = 5 * 60 * 1000; // 5 minutes

export interface PendingKycState {
  kycLink: string;
  timestamp: number;
}

/**
 * Save KYC link to localStorage (survives cross-domain redirects and page reloads)
 */
export function savePendingKycLink(kycLink: string): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    console.warn('[pendingKycState] localStorage not available');
    return;
  }

  const timestamp = Date.now();

  try {
    localStorage.setItem(PENDING_KYC_LINK_KEY, kycLink);
    localStorage.setItem(PENDING_KYC_TS_KEY, timestamp.toString());
    console.log('[pendingKycState] Saved pending KYC link:', { kycLink, timestamp });
  } catch (error) {
    console.error('[pendingKycState] Failed to save:', error);
  }
}

/**
 * Get pending KYC link from localStorage (with TTL enforcement)
 * Returns null if expired or not found
 */
export function getPendingKycLink(): PendingKycState | null {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const kycLink = localStorage.getItem(PENDING_KYC_LINK_KEY);
    const timestampStr = localStorage.getItem(PENDING_KYC_TS_KEY);

    if (!kycLink || !timestampStr) {
      return null;
    }

    const timestamp = parseInt(timestampStr, 10);
    const age = Date.now() - timestamp;

    // Check TTL
    if (age > PENDING_KYC_MAX_AGE) {
      console.log('[pendingKycState] Link expired (age:', age, 'ms), clearing');
      clearPendingKycLink();
      return null;
    }

    console.log('[pendingKycState] Retrieved pending KYC link:', { kycLink, age });
    return { kycLink, timestamp };
  } catch (error) {
    console.error('[pendingKycState] Failed to retrieve:', error);
    return null;
  }
}

/**
 * Clear pending KYC link from localStorage
 */
export function clearPendingKycLink(): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    localStorage.removeItem(PENDING_KYC_LINK_KEY);
    localStorage.removeItem(PENDING_KYC_TS_KEY);
    console.log('[pendingKycState] Cleared pending KYC link');
  } catch (error) {
    console.error('[pendingKycState] Failed to clear:', error);
  }
}
